import React from "react";

function NotificationPanel({
  open,
  onClose,
  notifications = [],
  onDismiss,
  onOpenTask,
  currentUser,
  darkMode = false,
}) {
  if (!open) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";

    try {
      const cleanDate = String(dateStr).slice(0, 10);
      const [year, month, day] = cleanDate.split("-");

      if (!year || !month || !day) return cleanDate;

      return new Date(year, month - 1, day).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch {
      return String(dateStr).slice(0, 10);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return "";

    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return String(value);
      return date.toLocaleString();
    } catch {
      return String(value);
    }
  };

  const getNotificationAssigneeName = (notification, task) => {
    if (notification?.assignee_name) {
      return notification.assignee_name;
    }

    if (notification?.assigned_to) {
      return notification.assigned_to;
    }

    if (notification?.user?.name) {
      return notification.user.name;
    }

    if (notification?.profiles?.name) {
      return notification.profiles.name;
    }

    if (currentUser?.name || currentUser?.full_name || currentUser?.email) {
      return currentUser.name || currentUser.full_name || currentUser.email;
    }

    const fallbackUser =
      JSON.parse(localStorage.getItem("user")) ||
      JSON.parse(sessionStorage.getItem("user"));

    return (
      fallbackUser?.name ||
      fallbackUser?.full_name ||
      fallbackUser?.email ||
      "Unknown User"
    );
  };


  const getPriority = (notification, task) => {
    return notification.priority || task?.priority || "Medium";
  };

  const getPriorityStyles = (priority) => {
    const value = String(priority || "Medium").toLowerCase();

    if (value === "high") {
      return {
        background: darkMode ? "#7f1d1d" : "#fee2e2",
        color: darkMode ? "#fecaca" : "#b91c1c",
        border: darkMode ? "1px solid #991b1b" : "1px solid #fecaca",
      };
    }

    if (value === "low") {
      return {
        background: darkMode ? "#14532d" : "#dcfce7",
        color: darkMode ? "#bbf7d0" : "#166534",
        border: darkMode ? "1px solid #166534" : "1px solid #bbf7d0",
      };
    }

    return {
      background: darkMode ? "#78350f" : "#fef3c7",
      color: darkMode ? "#fde68a" : "#92400e",
      border: darkMode ? "1px solid #92400e" : "1px solid #fde68a",
    };
  };

  const handleDismissClick = (notification) => {
    if (!onDismiss) return;
    onDismiss(notification.id || notification);
  };

  const handleOpenTask = (notification) => {
    const taskId =
      notification.task_id ||
      notification.task?.id ||
      notification.tasks?.id;

    if (!taskId) return;

    onClose && onClose();

    if (notification.type === "TASK_EXTENSION_REQUEST") {
      window.location.href = `/tasks/${taskId}?tab=extension`;
      return;
    }

    window.location.href = `/tasks/${taskId}`;
  };

  const renderTaskCreatedCard = (n, task) => {
    const isExtensionRequest = n.type === "TASK_EXTENSION_REQUEST";
    const priority = getPriority(n, task);
    const priorityStyles = getPriorityStyles(priority);

    const startDate = task?.start_date || task?.startDate || n.start_date;
    const dueDate = task?.due_date || task?.dueDate || n.due_date;

    const title =
      task?.title ||
      n.task_title ||
      n.title ||
      (isExtensionRequest ? "Task Extension Request" : "New Task Assigned");

    const description = isExtensionRequest
      ? (
          n.request_note ||
          n.message ||
          task?.request_note ||
          task?.extension_request_note ||
          "No request note added."
        )
      : (
          task?.task_note ||
          n.task_note ||
          n.message ||
          task?.description ||
          "You have been assigned a new task."
        );

    return (
      <div
        key={n.id}
        style={{
          padding: 14,
          borderRadius: 16,
          marginBottom: 12,
          background: darkMode ? "#1f2937" : isExtensionRequest ? "#eff6ff" : "#fffbeb",
          border: darkMode
            ? "1px solid #374151"
            : isExtensionRequest
              ? "2px solid #bfdbfe"
              : "2px solid #fde68a",
          boxShadow: darkMode
            ? "0 4px 12px rgba(0,0,0,0.3)"
            : "0 4px 12px rgba(245,158,11,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              minWidth: 0,
              flex: 1,
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <h4
                style={{
                  margin: 0,
                  fontSize: 15,
                  fontWeight: 700,
                  color: darkMode ? "#f9fafb" : "#111827",
                  lineHeight: 1.2,
                  wordBreak: "break-word",
                }}
              >
                {title}
              </h4>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginTop: 8,
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                    color: darkMode ? "#d1d5db" : "#6b7280",
                  }}
                >
                  {isExtensionRequest ? "Extension requested" : "Task assigned"}
                </span>

                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "4px 10px",
                    borderRadius: 999,
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: "uppercase",
                    letterSpacing: "0.03em",
                    ...priorityStyles,
                  }}
                >
                  {priority} Priority
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={() => handleDismissClick(n)}
            style={{
              border: "none",
              background: "transparent",
              color: darkMode ? "#d1d5db" : "#6b7280",
              fontSize: 17,
              cursor: "pointer",
              fontWeight: 400,
              height: 34,
              width: 34,
              lineHeight: "28px",
              flexShrink: 0,
            }}
            title="Dismiss notification"
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: darkMode ? "#111827" : "#ffffff",
            border: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10,
              marginBottom: 12,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: darkMode ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                }}
              >
                {isExtensionRequest ? "Requested By" : "Assigned To"}
              </div>

              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  color: darkMode ? "#f9fafb" : "#111827",
                }}
              >
                {isExtensionRequest
                  ? n.assignee_name || n.assigned_to || "Not available"
                  : getNotificationAssigneeName(n, task)}
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: darkMode ? "#9ca3af" : "#6b7280",
                  textTransform: "uppercase",
                }}
              >
                Timeline
              </div>

              <div
                style={{
                  marginTop: 6,
                  fontSize: 12,
                  fontWeight: 600,
                  color: darkMode ? "#f9fafb" : "#111827",
                  lineHeight: 1.35,
                }}
              >
                {formatDate(startDate)} → {formatDate(dueDate)}
              </div>
            </div>
          </div>

          <div
            style={{
              borderTop: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
              paddingTop: 14,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: darkMode ? "#9ca3af" : "#6b7280",
                textTransform: "uppercase",
              }}
            >
              {isExtensionRequest ? "Request Note" : "Description"}
            </div>

            <p
              style={{
                margin: "8px 0 0",
                fontSize: 12,
                fontWeight: 400,
                lineHeight: 1.5,
                color: darkMode ? "#d1d5db" : "#111827",
              }}
            >
              {description}
            </p>
          </div>
        </div>

        <div
          style={{
            marginTop: 12,
            display: "flex",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            onClick={() => handleOpenTask(n)}
            style={{
              minWidth: 82,
              padding: "7px 18px",
              borderRadius: 10,
              border: "none",
              background: isExtensionRequest ? "#2563eb" : "#76AB3F",
              color: "white",
              fontWeight: 700,
              fontSize: 11,
              cursor: "pointer",
              boxShadow: "0 8px 18px rgba(118,171,63,0.25)",
            }}
          >
            Open
          </button>
        </div>

        {n.created_at && (
          <div
            style={{
              marginTop: 10,
              fontSize: 11,
              fontWeight: 400,
              color: darkMode ? "#9ca3af" : "#9ca3af",
            }}
          >
            {formatDateTime(n.created_at)}
          </div>
        )}
      </div>
    );
  };

  const visibleNotifications = notifications.filter(
    (n) => n.type === "TASK_CREATED" || n.type === "TASK_EXTENSION_REQUEST"
  );

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.25)",
          zIndex: 1998,
        }}
      />

      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "390px",
          maxWidth: "92vw",
          height: "100vh",
          background: darkMode ? "#111827" : "#ffffff",
          color: darkMode ? "#f9fafb" : "#111827",
          boxShadow: "-10px 0 30px rgba(0,0,0,0.18)",
          zIndex: 1999,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            padding: "18px 20px",
            borderBottom: darkMode ? "1px solid #374151" : "1px solid #e5e7eb",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <h3 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>
              Notifications
            </h3>

            <p
              style={{
                margin: "4px 0 0",
                fontSize: 13,
                fontWeight: 400,
                color: darkMode ? "#9ca3af" : "#6b7280",
              }}
            >
              Task assignments and extension requests
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              border: "none",
              background: darkMode ? "#1f2937" : "#f3f4f6",
              color: darkMode ? "#f9fafb" : "#111827",
              borderRadius: "50%",
              width: 44,
              height: 44,
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 24,
              lineHeight: "44px",
            }}
            title="Close"
          >
            ×
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {visibleNotifications.length === 0 ? (
            <div
              style={{
                padding: 24,
                textAlign: "center",
                color: darkMode ? "#9ca3af" : "#6b7280",
              }}
            >
              No notifications
            </div>
          ) : (
            visibleNotifications.map((n) => {
              const task = n.task || n.tasks || null;
              return renderTaskCreatedCard(n, task);
            })
          )}
        </div>
      </div>
    </>
  );
}

export default NotificationPanel;